import { Nullable } from "@/types/util"
import { z } from "zod"

const MinPasswordLength = 8
const MaxPasswordLength = 72

export const PasswordSchema = z
  .string()
  .min(MinPasswordLength, `Must be at least ${MinPasswordLength} characters`)
  .max(MaxPasswordLength, `Must be at most ${MaxPasswordLength} characters`)
  .refine((password) => /[a-z]/.test(password), {
    message: "Must contain at least one lowercase letter",
  })
  .refine((password) => /[A-Z]/.test(password), {
    message: "Must contain at least one uppercase letter",
  })
  .refine((password) => /\d/.test(password), {
    message: "Must contain at least one number",
  })
  .refine((password) => /[!@#$%^&*()_+{}[\]:;<>,.?~\\/-]/.test(password), {
    message: "Must contain at least one symbol",
  })

export interface SetPasswordFormValue {
  password: string
  confirmPassword: string
}

export const SetPasswordFormSchema = z
  .object({
    password: PasswordSchema,
    confirmPassword: z.string(),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: "custom",
        path: ["confirmPassword"],
        message: "Passwords do not match",
      })
    }
  })

export type SetPasswordFormErrors = z.core.$ZodFormattedError<
  z.output<typeof SetPasswordFormSchema>
>

export type SetPasswordFormValidated = z.infer<typeof SetPasswordFormSchema>

export function validateSetPasswordFormValue(
  value: SetPasswordFormValue
): [true, SetPasswordFormValue, null] | [false, null, SetPasswordFormErrors] {
  const res = SetPasswordFormSchema.safeParse(value)

  if (res.success) {
    return [true, res.data, null]
  }

  return [false, null, z.formatError(res.error)]
}

export type SetPasswordState = [
  SetPasswordFormValue,
  Nullable<SetPasswordFormValidated>,
  Nullable<SetPasswordFormErrors>,
]

export function initialSetPasswordFormState(): SetPasswordState {
  const SetPasswordFormValue: SetPasswordFormValue = {
    password: "",
    confirmPassword: "",
  }
  const [, validated, errors] =
    validateSetPasswordFormValue(SetPasswordFormValue)
  return [SetPasswordFormValue, validated, errors]
}
